/**
 * Arena Wars net entry point.
 *
 * Single canonical façade over the self-hosted Colyseus realtime room +
 * Express/SQLite REST stack. Import `Arena` from this module wherever a
 * component needs realtime matchmaking, persistent saves, leaderboards,
 * inventory or the authenticated user.
 *
 *     import { Arena } from './net/arena.js';
 *     await Arena.ready;
 *     const slot = await Arena.save.load(1);
 *
 * No globals, no DOM events, no "SDK loaded" race — `Arena.ready` is a
 * promise that resolves once REST authentication completes (guest token is
 * issued automatically on first run).
 */
import { buildRestShims } from './rest-shims.js';
import { buildArenaShim } from './colyseus-adapter.js';

// True only in a real browser tab. The Wonderland editor parses components in
// a sandboxed Node-like env where `window` exists but lacks `location` /
// `document`, which crashes any naive boot code at packaging time.
function isBrowser() {
    return typeof window !== 'undefined'
        && typeof window.location !== 'undefined'
        && typeof document !== 'undefined';
}

function resolveConfig() {
    const cfg = (isBrowser() && window.__ARENA_NET__) || {};
    const origin = isBrowser() ? window.location.origin : '';
    const wsOrigin = origin.replace(/^http/, 'ws');
    return {
        wsUrl: cfg.wsUrl || wsOrigin,
        apiUrl: cfg.apiUrl || `${origin}/api`,
    };
}

/** Singleton built lazily so the WLE editor's component parser doesn't try
 *  to open a websocket during packaging. */
function build() {
    const { wsUrl, apiUrl } = resolveConfig();
    const rest = buildRestShims({ apiUrl });
    const lobby = buildArenaShim({ wsUrl, getAuth: rest.ensureAuth });

    // `ready` always resolves — never rejects — so consumers can simply
    // `await Arena.ready` without try/catch. On auth failure we still flip
    // ready (the individual REST calls will surface their own errors).
    const ready = rest.ensureAuth()
        .then(() => true)
        .catch((e) => {
            console.warn('[arena-net] auth failed, continuing unauthenticated', e);
            return false;
        });

    return {
        user:        rest.user,
        save:        rest.saveGame,
        leaderboard: rest.leaderboard,
        inventory:   rest.inventory,
        lobby,
        ready,
    };
}

// Lazy singleton. `null` in the editor (isBrowser() === false) so importing
// this module during component packaging stays side-effect free.
let _instance = null;
function getInstance() {
    if (!isBrowser()) return null;
    if (!_instance) _instance = build();
    return _instance;
}

/** Proxy so `Arena.x` works without callers thinking about lazy init. */
export const Arena = new Proxy({}, {
    get(_t, prop) {
        const inst = getInstance();
        return inst ? inst[prop] : undefined;
    },
});

// Convenience devtools hook — lets the user set their display name without a
// dedicated WLE UI. Example:
//     await window.Arena.user.setName('Patzn');
//     await window.Arena.user.register('Patzn', 'hunter2');
if (typeof window !== 'undefined') {
    try { window.Arena = Arena; } catch (e) { /* ignore */ }
}
